import React, { useState, useEffect } from 'react';
import './RemoteControl.css';

// PressableButton component to handle touch events
const PressableButton = ({ className, onClick, children }) => {
  const [isPressed, setIsPressed] = useState(false);

  const handlePress = () => {
    if (navigator.vibrate) {
      navigator.vibrate(50); // Vibrate for 50ms
    }
  };
  
  return (
    <button
      className={`button ${className} ${isPressed ? 'pressed' : ''}`}
      onTouchStart={() => {
        setIsPressed(true); // Trigger when touched
        handlePress(); // Trigger vibration
      }}
      onTouchEnd={() => setIsPressed(false)} // Reset when touch ends
      onMouseDown={() => setIsPressed(true)} // For desktop (optional)
      onMouseUp={() => setIsPressed(false)} // For desktop (optional)
      onMouseLeave={() => setIsPressed(false)} // Reset if mouse leaves button
      onClick={onClick} // Handle button click
    >
      {children}
    </button>
  );
};


// RemoteControl component to manage the remote control UI and functionality
const RemoteControl = () => {
  const [location, setLocation] = useState('');
  const [isLocationDetected, setIsLocationDetected] = useState(false); // Track if location was auto-detected
  const [room, setRoom] = useState('');
  const [slide, setSlide] = useState(1);

  const locations = {
    Buena_Park: ['Conference Room', 'Office1', 'Co-Packing', 'HPP', 'Break Room'],
    Wilmington: ['Conference Room', 'Office2', 'Office1', 'Break Room', 'Production', 'Prod. Conference']
  };

  // Detect location based on hostname
  useEffect(() => {
    const hostname = window.location.hostname;

    const detectLocationByHostname = () => {
      if (hostname.includes('bp1')) {
        return 'Buena_Park';
      } else if (hostname.includes('wm1')) {
        return 'Wilmington';
      }
      return null; // Default or unknown location
    };

    const detectedLocation = detectLocationByHostname();
    if (detectedLocation) {
      setLocation(detectedLocation);
      setIsLocationDetected(true); // Mark location as auto-detected
    }
  }, []);

  const handleLocationChange = (e) => {
    setLocation(e.target.value);
    setIsLocationDetected(false); // Mark location as manually selected
  };

  const networkPaths = {
    'Buena_Park': {
      'Conference Room': '\\\\bphpp-fs01\\BP HPPFS\\IT\\TVs\\BPConference',
      'Office1': '\\\\bphpp-fs01\\BP HPPFS\\IT\\TVs\\Office1',
      'Co-Packing': '\\\\bphpp-fs01\\BP HPPFS\\IT\\TVs\\Co-Packing',
      'HPP': '\\\\bphpp-fs01\\BP HPPFS\\IT\\TVs\\HPP',
      'Break Room': '\\\\bphpp-fs01\\BP HPPFS\\IT\\TVs\\BreakRoom'
    },
    'Wilmington': {
      'Conference Room': '\\\\hppfs-dc\\Production\\IT\\TVs\\WMConference',
      'Office2': '\\\\hppfs-dc\\Production\\IT\\TVs\\Office2',
      'Office1': '\\\\hppfs-dc\\Production\\IT\\TVs\\Office1',
      'Break Room': '\\\\hppfs-dc\\Production\\IT\\TVs\\BreakRoom',
      'Production': '\\\\hppfs-dc\\Production\\IT\\TVs\\Production',
      'Prod. Conference': '\\\\hppfs-dc\\Production\\IT\\TVs\\ProdConference'
    }
  };

  // Server URL mapping based on location
  const serverUrls = {
    'Buena_Park': 'https://dynppt.bp1.hppfs.org',
    'Wilmington': 'https://dynppt.wm1.hppfs.org'
  };

  const sendRequest = (filename, action) => {
    if (!location) {
      alert('Please select a location.');
      return;
    }
    if (!room) {
      alert('Please select a room.');
      return;
    }

    const network_path = networkPaths[location][room];
    if (!network_path) {
      alert('Invalid room selected.');
      return;
    }

    // Get the server URL based on the selected location
    const serverUrl = serverUrls[location];
    if (!serverUrl) {
      alert('Invalid location selected.');
      return;
    }

    const payload = {
      network_path: network_path,
      filename: filename,
      action: action,
    };

    console.log('Sending payload:', payload);

    fetch(`${serverUrl}/api/file-action/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        console.log('Command sent successfully:', data);
      })
      .catch(error => {
        console.error('There was a problem with the fetch operation:', error);
      });
  };

  // Parameter mapping based on location and room
  const parameterMapping = {
    'Buena_Park': {
      domain: 'bphpp', // Domain for Buena Park
      rooms: {
        'Conference Room': ['RunDynamicPresentation', 'BP-ConferenceRoom', 'bphpp\\bp.conference', 'Icpk2015'],
        'Office1': ['RunDynamicPresentation', 'BPHPP-228', 'bphpp\\tv.user', 'Icpk2015'],
        'Co-Packing': ['RunDynamicPresentation', 'BPHPP-230', 'bphpp\\copacking.tv', 'Icpk2015'],
        'HPP': ['RunDynamicPresentation', 'BPHPP-224', 'bphpp\\production.office', 'Icpk2015'],
        'Break Room': ['RunDynamicPresentation', 'BPHPP-000', 'username', 'pwd']
      }
    },
    'Wilmington': {
      domain: 'hppfs', // Domain for Wilmington
      rooms: {
        'Conference Room': ['RunDynamicPresentation', 'BPHPP-246', 'hppfs\\wm.conference', 'Icpk2015'],
        'Office2': ['RunDynamicPresentation', 'HPPFS-PC41', 'hppfs\\tv.user', 'Icpk2015'],
        'Office1': ['RunDynamicPresentation', 'HPPFS-PC42', 'hppfs\\tv.user', 'Icpk2015'],
        'Break Room': ['RunDynamicPresentation', 'HPPFS-PC40', 'hppfs\\tv.user', 'Icpk2015'],
        'Production': ['RunDynamicPresentation', 'HPPFS-PC28', 'hppfs\\tv.user', 'Icpk2015'],
        'Prod. Conference': ['RunDynamicPresentation', 'HPPFS-PC27', 'hppfs\\tv.user', 'Icpk2015']
      }
    }
  };

  const runTask = (task_name, computer_name = null, username = null, pwd = null, domain = null) => {
    if (!task_name) {
      alert('Task name parameter is missing.');
      return;
    }

    const formattedTaskName = task_name.startsWith('\\') ? task_name : `\\${task_name}`;
    const formattedComputerName = computer_name ? (computer_name.startsWith('\\\\') ? computer_name : `\\\\${computer_name}`) : null;
    const formattedUsername = username ? (username.includes('\\') ? username : `${domain}\\${username}`) : null;

    const payload = {
      task_name: formattedTaskName,
      computer_name: formattedComputerName,
      username: formattedUsername,
      pwd: pwd
    };

    // Get the server URL based on the selected location
    const serverUrl = serverUrls[location];
    if (!serverUrl) {
      alert('Invalid location selected.');
      return;
    }

    fetch(`${serverUrl}/api/run-task/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        console.log('Task executed successfully:', data);
      })
      .catch(error => {
        console.error('There was a problem with the fetch operation:', error);
      });
    console.log('Server URL:', serverUrl);
  };

  const handleRunTask = () => {
    if (!location || !room) {
      alert('Please select a location and a room.');
      return;
    }

    const locationData = parameterMapping[location];
    if (!locationData) {
      alert('Invalid location selected.');
      return;
    }

    const params = locationData.rooms[room];
    if (!params) {
      alert('Invalid room selected.');
      return;
    }

    const domain = locationData.domain;

    runTask(params[0], params[1], params[2], params[3], domain);
  };

  return (
    // Remote control UI
    <div className="page-container">
      <div className="remote-control">
        <div>
          <br />
          <h1>DynPPT Remote</h1>
          <br />
        </div>
        <div className="form-section">
          <label>Location:</label>
          <select
            className="dropdown"
            value={location || ''}
            onChange={handleLocationChange} // Allow manual selection
            disabled={isLocationDetected} // Disable only if location was auto-detected
          >
            <option value="" disabled>
              {location ? 'Detected Location' : 'Select Location'}
            </option>
            {Object.keys(locations).map((loc) => (
              <option key={loc} value={loc}>
                {loc}
              </option>
            ))}
          </select>
        </div>
        <div className="form-section">
          <label>Room:</label>
          <select
            className="dropdown"
            value={room}
            onChange={(e) => setRoom(e.target.value)}
            disabled={!location} // Disable dropdown if no location is selected
          >
            <option value="">Select Room</option>
            {location && locations[location].map((rm) => (
              <option key={rm} value={rm}>
                {rm}
              </option>
            ))}
          </select>
        </div>
        <div className="slide-section">
          <label>Slide number:</label>
          <div className="slide-control">
          <PressableButton className="buttonSlide" onClick={() => setSlide((prev) => Math.max(prev - 1, 1))}>
            {'<'}
          </PressableButton>
          <input type="text" value={slide} readOnly />
          <PressableButton className="buttonSlide" onClick={() => setSlide((prev) => Math.min(prev + 1, 10))}>
            {'>'}
          </PressableButton>
          </div>
        </div>
        <div className="go-continue-group">
          <div>
            <PressableButton className="buttonGo" onClick={() => sendRequest(`${slide}.slide`, 3)}>
              Go
            </PressableButton>
          </div>
          <div>
            <PressableButton className="buttonContinue" onClick={() => sendRequest(`${slide}.slide`, 2)}>
              Continue
            </PressableButton>
          </div>
        </div>
        <div className="start-stop-section">
          <div className="button-container">
            <PressableButton className="buttonStart" onClick={handleRunTask}>
              Start
            </PressableButton>
            <PressableButton className="buttonStop" onClick={() => sendRequest('stop', 1)}>
              Stop
            </PressableButton>
          </div>
        </div>
        <div className="footer">
          <p>Version 2.2</p>
          <p>&copy; 2026 ICPK Corp.</p>
        </div>
      </div>
    </div>
  );
};

export default RemoteControl;